import React, { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Sidebar, FLAT_NAV } from "./Sidebar";
import { CommandBar } from "./CommandBar";
import { useSession } from "@/state/session";
import { STORAGE_KEYS, loadJSON, saveJSON, DESTINATION_BY_PATH } from "@/lib/prefs";
import { LayoutDashboard, ScanSearch, Bookmark, History, Compass } from "lucide-react";
import { NavLink } from "react-router-dom";

const MOBILE_NAV = [
  { to: "/app/analyzer", icon: ScanSearch, label: "Analyze" },
  { to: "/app/finder", icon: Compass, label: "Find" },
  { to: "/app/leads", icon: Bookmark, label: "Saved" },
  { to: "/app/history", icon: History, label: "History" },
  { to: "/app/dashboard", icon: LayoutDashboard, label: "Home" },
];

export function AppShell() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("ez_sidebar_collapsed") === "1");
  const loc = useLocation();
  const nav = useNavigate();
  const { session } = useSession();

  useEffect(() => { if (!session) nav("/signin"); }, [session, nav]);
  useEffect(() => { localStorage.setItem("ez_sidebar_collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => {
    if (DESTINATION_BY_PATH[loc.pathname]) {
      const prev = loadJSON(STORAGE_KEYS.recentPages, []).filter((p) => p !== loc.pathname);
      saveJSON(STORAGE_KEYS.recentPages, [loc.pathname, ...prev].slice(0, 8));
    }
  }, [loc.pathname]);

  const entry = FLAT_NAV.find((e) => e.to === loc.pathname)
    || FLAT_NAV.find((e) => e.to && loc.pathname.startsWith(e.to));
  const title = entry?.label || "Ezisell";
  const breadcrumb = entry?.group || "Ezisell";

  return (
    <div className="min-h-screen bg-surface-1">
      {/* desktop sidebar */}
      <div className="hidden md:block"><Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} /></div>

      <div className={cn("transition-[margin] duration-200 ease-out",
        "md:ml-[240px]", collapsed && "md:!ml-[72px]")}>
        <CommandBar title={title} breadcrumb={breadcrumb} onOpenScan={() => nav("/app/receipts")} />
        <main data-testid="app-main" className="px-4 lg:px-6 pt-5 pb-24 md:pb-10 max-w-[1720px] mx-auto ez-fade-in" key={`${loc.pathname}${loc.search}`}>
          <Outlet />
        </main>
      </div>

      {/* mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 h-14 bg-navy-900 border-t border-navy-800 flex">
        {MOBILE_NAV.map((m) => {
          const Icon = m.icon;
          return (
            <NavLink key={m.to} to={m.to} data-testid={`mnav-${m.label.toLowerCase()}`}
              className={({ isActive }) => cn("flex-1 flex flex-col items-center justify-center gap-0.5 text-micro",
                isActive ? "text-emerald-500" : "text-ink-inverse/60")}>
              <Icon className="h-5 w-5" /> {m.label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
